import React, {useEffect, useState} from "react";
import {
    useActivateProductsMutation,
    useDeactivateProductsMutation,
    useDeleteProductMutation,
    useGetAllMyProductsQuery,
} from "../graphql/generated/hooks";
import {useTranslation} from "react-i18next";
import {
    Button,
    Col,
    Dropdown,
    MenuProps,
    Radio,
    Row,
    Space,
    Tag,
    Typography,
    message,
    Table,
    Tooltip,
} from "antd";
import {useNavigate} from "react-router-dom";
import RobinPage from "../components/core/RobinPage";
import {faRotateRight} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import EditDeleteActions from "../components/EditDeleteActions";
import ProductCard from "../components/ProductCard";
import SafeImage from "../components/core/SafeImage";
import {
    AppstoreOutlined,
    CheckOutlined,
    CopyOutlined,
    DeleteOutlined,
    DownOutlined,
    StopOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import {
    LOCAL_STORAGE_KEYS,
    PRODUCT_IMAGE_PLACHOLDER,
    VIEW_MODE,
} from "../common/constants";

function ProductList() {
    const {t} = useTranslation();
    const [messageApi, contextHolder] = message.useMessage();
    const navigate = useNavigate();

    const [checkedList, setCheckedList] = useState<number[]>([]);
    const [viewMode, setViewMode] = useState<VIEW_MODE>(
        (localStorage.getItem(
            LOCAL_STORAGE_KEYS.PRODUCTS_VIEW_MODE
        ) as VIEW_MODE) || VIEW_MODE.GRID
    );

    const {data: products, loading, refetch} = useGetAllMyProductsQuery();
    const [deleteProduct, {loading: deleting}] = useDeleteProductMutation();
    const [activateProducts] = useActivateProductsMutation();
    const [deactivateProducts] = useDeactivateProductsMutation();

    useEffect(() => {
        refetch();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEYS.PRODUCTS_VIEW_MODE, viewMode);
    }, [viewMode]);

    const items: MenuProps["items"] = [
        {
            label: t("button.activate"),
            key: "1",
            icon: <CheckOutlined/>,
            onClick: () => {
                activateProducts({variables: {ids: checkedList}})
                    .then(() => {
                        messageApi.success(t("message.success.data_updated"));
                        refetch();
                    })
                    .catch((error) => {
                        messageApi.error(t("message.error.data_update_failed"));
                    });

                setCheckedList([]);
            },
        },
        {
            label: t("button.deactivate"),
            key: "2",
            icon: <StopOutlined/>,
            onClick: () => {
                deactivateProducts({variables: {ids: checkedList}})
                    .then(() => {
                        messageApi.success(t("message.success.data_updated"));
                        refetch();
                    })
                    .catch((error) => {
                        messageApi.error(t("message.error.data_update_failed"));
                    });

                setCheckedList([]);
            },
        },
        {
            label: t("button.delete"),
            key: "3",
            danger: true,
            icon: <DeleteOutlined/>,
            onClick: () => {
                checkedList.map((id) => {
                    return deleteProduct({variables: {id}})
                        .then(() => {
                            messageApi.success(t("message.success.data_deleted"));
                            refetch();
                        })
                        .catch((error) => {
                            messageApi.error(t("message.error.data_delete_failed"));
                        });
                });
                setCheckedList([]);
            },
        },
    ];
    const dataSource = products?.getAllProductsForPartner?.map((product) => ({
        key: product.id,
        imageUrl: product.image?.url || PRODUCT_IMAGE_PLACHOLDER,
        name: product.name,
        nameAr: product.nameAr,
        price: product.price,
        category: product.productCategory?.name,
        stores:
            product.stores?.map((store) => {
                const productStore = product.productStores?.find(
                    (ps) => ps.storeId === store.id
                );
                const outOfStock = productStore?.outOfStock === true;
                return { id: store.id, name: store.name, outOfStock };
            }) || [],
        actions: (
            <Space size="small" style={{ whiteSpace: "nowrap" }}>
                <Tooltip title={t("button.duplicate") || "Duplicate"}>
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                            navigate(`/menu/products/mutate`, {
                                state: { duplicateProductId: product.id },
                            });
                        }}
                    >
                        {t("button.duplicate") || "Duplicate"}
                    </Button>
                </Tooltip>
                <EditDeleteActions
                    onEdit={() => {
                        navigate(`/menu/products/mutate/${product.id}`);
                    }}
                    onDelete={() => {
                        deleteProduct({variables: {id: product.id}})
                            .then(() => {
                                messageApi.success(t("message.success.data_deleted"));
                                refetch();
                            })
                            .catch((error) => {
                                messageApi.error(t("message.error.data_delete_failed"));
                            });
                    }}
                    loadingDelete={deleting}
                />
            </Space>
        ),
    }));
    const columns = [
        {
            title: t("label.image", { defaultValue: "Image" }),
            dataIndex: "imageUrl",
            key: "imageUrl",
            render: (_: any, {imageUrl}: { imageUrl: string }) =>
                imageUrl && imageUrl.length > 0 ? (
                    <SafeImage src={imageUrl} alt="Product" style={{width: 50}} />
                ) : null,
        },
        {
            title: t("label.name"),
            dataIndex: "name",
            key: "name",
        },
        {
            title: t("label.name_ar"),
            dataIndex: "nameAr",
            key: "nameAr",
        },
        {
            title: t("label.price"),
            dataIndex: "price",
            key: "price",
            render: (_: any, {price}: { price: number }) => (
                <>{price === 0 ? <Tag color="blue">{t("label.free")}</Tag> : price}</>
            ),
        },
        {
            title: t("label.menu.product_categories", { defaultValue: "Category" }),
            dataIndex: "category",
            key: "category",
        },
        {
            title: t("label.menu.stores", { defaultValue: "Stores" }),
            dataIndex: "stores",
            key: "stores",
            ellipsis: true,
            render: (
                _: any,
                {stores}: { stores: { id: number; name: string; outOfStock: boolean }[] }
            ) => (
                <>
                    {stores.map((store) => (
                        <Tag color={store.outOfStock ? "red" : "blue"} key={store.id}>
                            {store.name}
                            {store.outOfStock
                              ? ` (${t("label.out_of_stock", { defaultValue: "Out of stock" })})`
                              : ""}
                        </Tag>
                    ))}
                </>
            ),
        },
        {
            title: t("label.actions"),
            dataIndex: "actions",
            key: "actions",
            width: 220,
            align: "center" as const,
            onCell: () => ({
                style: { whiteSpace: "nowrap" },
            }),
        },
    ];

    return (
        <RobinPage
            title={t("label.menu.products")}
            actions={[
                {
                    label: (
                        <Typography.Link
                            onClick={() => {
                                refetch();
                            }}
                        >
                            <FontAwesomeIcon icon={faRotateRight}/>
                        </Typography.Link>
                    ),
                },
                {
                    label: (
                        <Button
                            onClick={() => {
                                navigate("/menu/products/mutate");
                            }}
                        >
                            {t("button.add")}
                        </Button>
                    ),
                },
                {
                    label: (
                        <Radio.Group
                            onChange={(value) => {
                                setViewMode(value.target.value);
                                setCheckedList([]);
                            }}
                            value={viewMode}
                        >
                            <Radio.Button value={VIEW_MODE.GRID}>
                                <AppstoreOutlined/>
                            </Radio.Button>
                            <Radio.Button value={VIEW_MODE.LIST}>
                                <UnorderedListOutlined/>
                            </Radio.Button>
                        </Radio.Group>
                    ),
                },
                {
                    label: checkedList.length ? (
                        <Dropdown
                            menu={{
                                items,
                            }}
                        >
                            <Button>
                                <Space>
                                    {t("button.actions")}
                                    <DownOutlined/>
                                </Space>
                            </Button>
                        </Dropdown>
                    ) : (
                        <></>
                    ),
                },
            ]}
        >
            {contextHolder}
            {viewMode === VIEW_MODE.GRID ? (
                <Row gutter={[15, 15]}>
                    {products?.getAllProductsForPartner.map((product, index) => (
                        <Col
                            key={product.id}
                            xl={{span: 5, offset: index % 4 === 0 ? 2 : 0}}
                            lg={6}
                            md={8}
                            xs={24}
                        >
                            <ProductCard
                                loading={loading}
                                name={product?.name}
                                category={product?.productCategory?.name}
                                price={product?.price}
                                coverImageURL={product?.image?.url}
                                productId={product.id}
                                onUpdated={() => {
                                    refetch();
                                }}
                                stores={(product.stores || []).map((store) => {
                                    const productStore = product.productStores?.find(
                                        (ps) => ps.storeId === store.id
                                    );
                                    return {
                                        id: store.id,
                                        name: store.name,
                                        outOfStock: productStore?.outOfStock === true,
                                    };
                                })}
                                onCheck={(checked) => {
                                    if (checked) setCheckedList([...checkedList, product.id]);
                                    else
                                        setCheckedList(
                                            checkedList.filter((id) => id !== product.id)
                                        );
                                }}
                                onEdit={() => {
                                    navigate(`/menu/products/mutate/${product.id}`);
                                }}
                                onDuplicate={() => {
                                    navigate(`/menu/products/mutate`, {
                                        state: { duplicateProductId: product.id },
                                    });
                                }}
                            />
                        </Col>
                    ))}
                </Row>
            ) : (
                <Row gutter={[10, 10]}>
                    <Col span={24}>
                        <Table
                            rowSelection={{
                                type: "checkbox",
                                onChange: (selectedRowKeys: React.Key[]) => {
                                    setCheckedList(selectedRowKeys.map((key) => Number(key)));
                                },
                            }}
                            loading={loading}
                            dataSource={dataSource}
                            columns={columns}
                        />
                    </Col>
                </Row>
            )}
        </RobinPage>
    );
}

export default ProductList;
